import { useCallback, useEffect, useState } from 'react';
import {
  bulkProvisionLearners,
  fetchFacilityJobs,
  fetchFacilityProvisionStatus,
  fetchJobs,
  provisionFacilityToCanvas,
  retryJob,
} from './services/lms-provisioning.service';

/**
 * Drives the provisioning jobs panel: loads jobs (facility-scoped when a
 * facilityId is given, otherwise global) and pulls the chain-readiness
 * snapshot from `GET /facilities/:id/provision-canvas` on mount. Use the
 * panel Refresh button or reopen the modal to reload; there is no automatic
 * polling while jobs are in progress. Action helpers refresh readiness in
 * the same pass as jobs so the UI stays coherent.
 *
 * @param {{ facilityId?: number|null, paging?: { limit?: number, offset?: number } }} options
 */
const useProvisioningJobsHook = ({ facilityId = null, paging } = {}) => {
  const [jobs, setJobs] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadJobs = useCallback(
    async ({ background = false, skipReadiness = false } = {}) => {
      if (!background) {
        setLoading(true);
      }
      setError(null);

      const jobsPromise = facilityId != null ? fetchFacilityJobs(facilityId, paging) : fetchJobs(paging);
      const fetchReadiness = facilityId != null && !skipReadiness;
      const readinessPromise = fetchReadiness ? fetchFacilityProvisionStatus(facilityId) : Promise.resolve(null);

      const [jobsOutcome, readinessOutcome] = await Promise.allSettled([jobsPromise, readinessPromise]);

      if (jobsOutcome.status === 'fulfilled') {
        const jobsResult = jobsOutcome.value;
        setJobs(Array.isArray(jobsResult) ? jobsResult : jobsResult?.jobs ?? []);
      } else {
        setError(jobsOutcome.reason);
      }

      if (fetchReadiness) {
        if (readinessOutcome.status === 'fulfilled') {
          setReadiness(readinessOutcome.value ?? null);
        } else if (!background) {
          console.warn('[useProvisioningJobsHook] readiness load failed', readinessOutcome.reason);
        }
      } else if (facilityId == null) {
        setReadiness(null);
      }

      if (!background) {
        setLoading(false);
      }
    },
    [facilityId, paging],
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const isReadinessShape = (value) =>
    value !== null &&
    typeof value === 'object' &&
    'chainReady' in value &&
    'facilityProvisioned' in value &&
    Array.isArray(value.chain);

  const runAction = useCallback(
    async (action) => {
      setSubmitting(true);
      setError(null);
      try {
        const accepted = await action();
        const acceptedIsReadiness = isReadinessShape(accepted);
        if (acceptedIsReadiness) {
          setReadiness(accepted);
        }
        await loadJobs({ background: true, skipReadiness: acceptedIsReadiness });
        return accepted;
      } catch (err) {
        setError(err);
      } finally {
        setSubmitting(false);
      }
    },
    [loadJobs],
  );

  const provisionFacility = useCallback(
    (targetFacilityId) => runAction(() => provisionFacilityToCanvas(targetFacilityId ?? facilityId)),
    [runAction, facilityId],
  );

  const provisionLearners = useCallback(
    (targetFacilityId) => runAction(() => bulkProvisionLearners(targetFacilityId ?? facilityId)),
    [runAction, facilityId],
  );

  const retry = useCallback((jobId) => runAction(() => retryJob(jobId)), [runAction]);

  return {
    jobs,
    readiness,
    loading,
    error,
    submitting,
    refresh: () => loadJobs({ background: jobs.length > 0 }),
    provisionFacility,
    provisionLearners,
    retry,
  };
};

export default useProvisioningJobsHook;
